<script setup lang="ts">
/**
 * **节点标题**（显示 + 就地改名）。
 *
 * ## 为什么单独拆出来
 *
 * 它原来是 `NpNodeHeader.vue` 里的一段 —— 而那个文件贴着"行组件不超过
 * 200 行"那条线。2027 给头部加类型图标 + 动作按钮时正是它越了线；
 * 拆的时候没有按"哪里好切"切，而是按**语义**切：标题的显示与就地改名
 * 自成一体（一个状态机：显示 ↔ 输入框），和它旁边那些徽标不是一回事。
 *
 * ## 改名是**边打字边生效**
 *
 * `@input` 直接往上抛，没有「提交」这一步：失焦、点别处、直接关页面，
 * 名字都已经生效了（用户按 Esc 是唯一明确的"放弃"）。
 */
import { nextTick, ref } from 'vue'

const props = defineProps<{
  /** 类型给的默认名 */
  title: string
  /** 用户改过的名字（没有就是空） */
  label?: string
}>()

const emit = defineEmits<{ (e: 'rename', label: string): void }>()

const renaming = ref(false)
const renameEl = ref<HTMLInputElement | null>(null)

function startRename() {
  renaming.value = true
  void nextTick(() => {
    renameEl.value?.focus()
    renameEl.value?.select()
  })
}

/** 输入框里的原文 → 要发出去的 label（空 / 等于类型名 = 回到默认名） */
function prefixed(value: string): string {
  const next = value.trim()
  return next === props.title ? '' : next
}

/** Esc = 放弃这次改名，恢复原名 */
function cancelRename() {
  renaming.value = false
  emit('rename', props.label ?? '')
}
</script>

<template>
  <input
    v-if="renaming"
    ref="renameEl"
    class="np-node__title np-node__title--input nodrag"
    :value="label ?? title"
    spellcheck="false"
    @pointerdown.stop
    @click.stop
    @input="emit('rename', prefixed(($event.target as HTMLInputElement).value))"
    @keydown.enter.prevent="renaming = false"
    @keydown.escape.prevent="cancelRename"
    @blur="renaming = false"
  />
  <span
    v-else
    class="np-node__title"
    :title="`${label || title}（双击改名）`"
    @dblclick.stop="startRename"
  >
    {{ label || title }}
  </span>
</template>
